import { LogOut, Menu, Settings, User } from "lucide-react";

import NotificationsBell from "../../components/Shared/admin/NotificationsBell";
import type { TopBarProps } from "../types/topbar.types";
import { useTopBarHook } from "../hooks/useTopBarHook";

export default function TopBar({ onMenuToggle, activeMenuItem }: TopBarProps) {
  const {
    mounted,
    user,
    anchorEl,
    isProfileMenuOpen,
    isSmallMobile,
    handleProfileMenuOpen,
    handleProfileMenuClose,
    handleMenuToggle,
    handleLogout,
  } = useTopBarHook({
    onMenuToggle,
  });

  return (
    <div className="flex min-h-[48px] items-center justify-between bg-slate-50 px-4 py-4 sm:min-h-[56px] sm:px-6 sm:py-5 md:min-h-[64px] md:px-10 md:py-6">
      <div className="flex min-w-0 flex-1 items-center gap-4 sm:gap-6 md:gap-8">
        <button
          type="button"
          onClick={handleMenuToggle}
          className="block p-2 text-slate-500 md:hidden"
        >
          <Menu size={18} />
        </button>

        <h1 className="min-w-0 truncate text-[0.95rem] font-semibold text-slate-800 sm:text-[1.125rem] md:text-[1.375rem]">
          {activeMenuItem || "Dashboard"}
        </h1>
      </div>

      {mounted && (
        <div className="flex min-w-0 items-center gap-2 sm:gap-3 md:gap-4">
          <NotificationsBell />

          <button
            type="button"
            onClick={handleProfileMenuOpen}
            className="flex min-w-0 items-center gap-3 rounded-lg p-2 transition hover:bg-slate-100 md:rounded-xl"
          >
            <img
              src="https://icones.pro/wp-content/uploads/2021/02/icone-utilisateur-rouge.png"
              alt="User avatar"
              className="h-5 w-5 rounded-full object-cover sm:h-6 sm:w-6 md:h-7 md:w-7"
            />

            {!isSmallMobile && user && (
              <div className="min-w-0 text-left">
                <p className="truncate text-[0.7rem] font-medium text-slate-800 md:text-[0.75rem]">
                  {user.firstName} {user.lastName}
                </p>
                {user.roleLabel && (
                  <p
                    data-testid="topbar-role"
                    title={user.roleTooltip}
                    className="truncate text-[0.625rem] text-slate-500 md:text-[0.6875rem]"
                  >
                    {user.roleLabel}
                  </p>
                )}
              </div>
            )}
          </button>
        </div>
      )}

      {isProfileMenuOpen && (
        <div
          className="fixed inset-0 z-[1400]"
          onClick={handleProfileMenuClose}
        >
          <div
            className="absolute z-[1500] min-w-[140px] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:min-w-[160px] md:rounded-2xl"
            style={{
              top:
                anchorEl?.getBoundingClientRect().bottom !== undefined
                  ? anchorEl.getBoundingClientRect().bottom + window.scrollY + 8
                  : 70,
              left:
                anchorEl?.getBoundingClientRect().right !== undefined
                  ? anchorEl.getBoundingClientRect().right +
                    window.scrollX -
                    160
                  : window.innerWidth - 180,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={handleProfileMenuClose}
              className="flex w-full items-center gap-2 px-4 py-3 text-left text-[0.75rem] text-slate-700 transition hover:bg-slate-50 sm:px-5 sm:text-[0.8125rem]"
            >
              <User size={16} className="text-slate-500" />
              Profile
            </button>

            <button
              type="button"
              onClick={handleProfileMenuClose}
              className="flex w-full items-center gap-2 px-4 py-3 text-left text-[0.75rem] text-slate-700 transition hover:bg-slate-50 sm:px-5 sm:text-[0.8125rem]"
            >
              <Settings size={16} className="text-slate-500" />
              Settings
            </button>

            <div className="border-t border-slate-200" />

            <button
              type="button"
              onClick={handleLogout}
              className="flex w-full items-center gap-2 px-4 py-3 text-left text-[0.75rem] text-slate-700 transition hover:bg-red-50 sm:px-5 sm:text-[0.8125rem]"
            >
              <LogOut size={16} className="text-red-500" />
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
